import {
  AppNoticeBanner,
  type NoticeTone,
} from "@/components/ui/AppNoticeBanner";
import { useHubCtas } from "@/hooks/useHubCtas";
import { useUserSubscriptionState } from "@/hooks/useUserSubscriptionState";
import { useAuth } from "@/lib/AuthContext";
import { useAppleTrial } from "@/lib/purchases/useAppleTrial";
import { resolveAppLink } from "@/lib/resolveAppLink";
import { TRIAL_CTA_TARGET } from "@/lib/trialCourseCta";
import { mixpanelService } from "@/services/mixpanel.service";
import type { HubCta, HubCtaTarget } from "@/types/hubCtas";
import { usePathname } from "expo-router";
import React, { useCallback, useMemo } from "react";
import { YStack } from "tamagui";

type Props = {
  userId: string | undefined;
  /** Render only CTAs with these targets (e.g. the trial CTA on a dashboard). */
  only?: HubCtaTarget[];
};

type CtaItem = {
  key: string;
  tone: NoticeTone;
  title: string;
  summary: string;
  label: string;
  href: string | null;
  imageUrl: string | null;
  cta: HubCta;
};

const PRIMARY_TONE: Record<HubCtaTarget, NoticeTone> = {
  non_member: "join",
  regular: "join",
  monthly: "upgrade",
  trial_beginner_piano: "promo",
};

/** Alternate tones when multiple CTAs share a target (1st primary, 2nd alt, 3rd+ neutral). */
function toneForStack(target: HubCtaTarget, indexInTarget: number): NoticeTone {
  const primary = PRIMARY_TONE[target];
  if (indexInTarget <= 0) return primary;
  if (indexInTarget === 1) {
    return primary === "upgrade" ? "join" : "upgrade";
  }
  return "neutral";
}

function toItem(cta: HubCta, indexInTarget: number): CtaItem {
  const { href } = resolveAppLink(cta.link);
  return {
    key: `${cta.target}-${cta.id}`,
    tone: toneForStack(cta.target, indexInTarget),
    title: cta.title,
    summary: cta.summary,
    label: cta.cta_label,
    href: href || null,
    imageUrl: cta.image_url,
    cta,
  };
}

/**
 * Hub CTAs from `get_active_hub_ctas` — audience from subscription (+ join
 * date and survey answers for the trial target), copy from CMS.
 */
export function SubscriptionHubCtas({ userId, only }: Props) {
  const { user, preferenceMetadata, preferencesLoading } = useAuth();
  const pathname = usePathname();
  // isSuccess (not isFetched): a failed fetch must not read as "no row" and
  // show Join to a member.
  const { data: row = null, isSuccess } = useUserSubscriptionState({ userId });
  const appleTrial = useAppleTrial(userId);
  const { data: ctas = [] } = useHubCtas({
    userId,
    subscription: row,
    subscriptionResolved: isSuccess,
    userCreatedAt: user?.created_at ?? null,
    preferenceMetadata,
    preferencesLoading,
    appleTrial,
  });

  const items = useMemo(() => {
    // An enabled trial CTA replaces every other banner for that user; with no
    // trial row in the CMS the regular banners show exactly as before.
    const trialRows = ctas.filter((cta) => cta.target === TRIAL_CTA_TARGET);
    const visible = trialRows.length ? trialRows : ctas;
    const seen: Partial<Record<HubCtaTarget, number>> = {};
    return visible
      .filter((cta) => !only || only.includes(cta.target))
      .map((cta) => {
        const i = seen[cta.target] ?? 0;
        seen[cta.target] = i + 1;
        return toItem(cta, i);
      });
  }, [ctas, only]);

  const onTrack = useCallback(
    async (cta: HubCta) => {
      await mixpanelService.trackHubCtaClick(
        {
          id: cta.id,
          target: cta.target,
          title: cta.title,
          link: resolveAppLink(cta.link).href,
        },
        user,
        pathname,
      );
      await mixpanelService.flush();
    },
    [pathname, user],
  );

  if (!items.length) return null;

  return (
    <YStack w="100%" gap="$2" mb="$3">
      {items.map((item) => (
        <AppNoticeBanner
          key={item.key}
          tone={item.tone}
          title={item.title}
          summary={item.summary}
          ctaLabel={item.label}
          href={item.href}
          imageUrl={item.imageUrl}
          onPress={item.href ? () => void onTrack(item.cta) : undefined}
          withSpacing={false}
        />
      ))}
    </YStack>
  );
}
